import { useState, type FormEvent, type JSX } from 'react';
import { makeApi } from '../../lib/api';
import { useAuthStore } from './authStore';

/**
 * Phase-3 step-2 — after the User registered (or after they logged in
 * and have zero spaces), prompt them to create their first Space.
 * Calls `POST /v1/spaces` which seeds the workflow + blueprint
 * library and returns the new slug.
 */
export function CreateFirstSpaceForm(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const setUserAndSpaces = useAuthStore((s) => s.setUserAndSpaces);
  const setCurrentSpace = useAuthStore((s) => s.setCurrentSpace);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('de-AT');
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (token === null) return;
    setStatus({ kind: 'busy' });

    const api = makeApi(token);
    const result = await api.createSpace({
      slug: slug.trim().toLowerCase(),
      name: name.trim(),
      default_locale_code: locale,
    });

    if (result.status === 'ok') {
      // Refresh /me so the editor knows about the new space.
      const me = await api.me();
      if (me.status === 'ok') {
        setUserAndSpaces(me.data.user, me.data.spaces);
        setCurrentSpace(result.data.slug);
      }
      return;
    }

    if (result.status === 'error' && result.code === 422) {
      setStatus({
        kind: 'error',
        message: 'Slug muss eindeutig sein (Kleinbuchstaben + Bindestrich, beginnt mit Buchstabe).',
      });
      return;
    }
    setStatus({
      kind: 'error',
      message:
        result.status === 'error' ? `HTTP ${result.code}` : 'Anlegen fehlgeschlagen',
    });
  }

  return (
    <main className="grid min-h-screen place-items-center bg-zinc-50 px-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Erste Website anlegen
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            {user?.name ? `Willkommen, ${user.name}. ` : ''}Lege deinen
            ersten Space (Website) an. Du kannst später beliebig viele
            ergänzen.
          </p>
        </header>

        <div className="flex flex-col gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              Slug (URL-Teil)
            </span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="meine-firma"
              required
              pattern="^[a-z][a-z0-9\-]*$"
              data-testid="create-space-slug"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 font-mono text-sm focus:border-zinc-900 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              Anzeigename
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Meine Firma"
              required
              data-testid="create-space-name"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              Default-Sprache
            </span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              data-testid="create-space-locale"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            >
              <option value="de-AT">Deutsch (Österreich)</option>
              <option value="de-DE">Deutsch (Deutschland)</option>
              <option value="en-GB">English (UK)</option>
              <option value="en-US">English (US)</option>
              <option value="es-ES">Español</option>
              <option value="fr-FR">Français</option>
            </select>
          </label>
        </div>

        {status.kind === 'error' ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            {status.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status.kind === 'busy'}
          data-testid="create-space-submit"
          className="mt-6 w-full rounded-md bg-zinc-900 px-4 py-2 font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {status.kind === 'busy' ? 'Wird angelegt…' : 'Website anlegen'}
        </button>

        <p className="mt-6 text-xs text-zinc-500">
          <button
            type="button"
            onClick={signOut}
            className="underline hover:text-zinc-700"
          >
            Abmelden
          </button>
        </p>
      </form>
    </main>
  );
}
